import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import { extractSchoolSlugFromPath, isPublicPath } from '../utils/routing';
import { signalConsentRequired } from './consentSignal';
import { isSessionEnded, refreshAccessToken } from './tokenRefresh';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
const API_BASE = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

// ===========================================
// CSRF Token Management
// ===========================================

let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;

/**
 * Fetch CSRF token from server
 * Uses a singleton promise to prevent multiple simultaneous fetches
 */
async function fetchCsrfToken(): Promise<string> {
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }

  csrfTokenPromise = axios
    .get<{ csrfToken: string }>(`${API_BASE}/api/csrf-token`, {
      withCredentials: true,
    })
    .then((response) => {
      csrfToken = response.data.csrfToken;
      csrfTokenPromise = null;
      return csrfToken;
    })
    .catch((error) => {
      csrfTokenPromise = null;
      console.error('Failed to fetch CSRF token:', error);
      throw error;
    });

  return csrfTokenPromise;
}

/**
 * Get current CSRF token, fetching if necessary
 */
async function getCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }
  return fetchCsrfToken();
}

/**
 * Clear CSRF token (called on 403 to force refresh)
 */
function clearCsrfToken(): void {
  csrfToken = null;
}

/**
 * Initialize CSRF token on app load
 * Call this early in app initialization
 */
export async function initializeCsrf(): Promise<void> {
  try {
    await fetchCsrfToken();
  } catch (error) {
    console.warn('CSRF token initialization failed, will retry on first request');
  }
}

// ===========================================
// Tenant Context Helpers
// ===========================================

/**
 * Get current school slug from URL
 * Used to inject tenant context into API requests
 */
function getCurrentSchoolSlug(): string | null {
  return extractSchoolSlugFromPath(window.location.pathname);
}


// ===========================================
// Axios Instance
// ===========================================

// Create axios instance with dynamic baseURL
function getBaseURL(url?: string): string {
  // Public endpoints that should NOT include school slug
  // Use startsWith to avoid matching /google-drive/auth/ etc
  const publicPrefixes = ['/auth/', '/csrf-token', '/public/'];
  const isPublicEndpoint = url && publicPrefixes.some(prefix => url.startsWith(prefix));

  // Don't add school slug for public endpoints
  if (isPublicEndpoint) {
    return API_URL;
  }

  const schoolSlug = getCurrentSchoolSlug();
  if (schoolSlug) {
    return `${API_BASE}/${schoolSlug}/api/v1`;
  }
  return API_URL;
}

const api: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// ===========================================
// Request Interceptor
// ===========================================

// Methods that require CSRF protection
const CSRF_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// Request interceptor to add auth token, CSRF token, and tenant context
api.interceptors.request.use(
  async (config) => {
    // Set baseURL dynamically based on current school slug (excluding public endpoints)
    config.baseURL = getBaseURL(config.url);

    // Add auth token (but NOT for public/login endpoints)
    const publicEndpoints = ['/auth/login', '/auth/refresh', '/auth/2fa/verify', '/public/', '/csrf-token'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

    if (!isPublicEndpoint) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add CSRF token for state-changing methods
    if (config.method && CSRF_METHODS.includes(config.method.toUpperCase())) {
      try {
        // Add timeout to prevent hanging if CSRF fetch is stuck
        const csrfPromise = getCsrfToken();
        const timeoutPromise = new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('CSRF token fetch timeout')), 5000)
        );
        const csrf = await Promise.race([csrfPromise, timeoutPromise]);
        config.headers['x-csrf-token'] = csrf;
      } catch (error) {
        // If CSRF fetch fails, continue anyway - server will reject if needed
        console.warn('Could not get CSRF token for request:', error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ===========================================
// Response Interceptor
// ===========================================

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
      _csrfRetry?: boolean;
      _staleReplay?: boolean;
    };

    // Handle CSRF token mismatch (403 with CSRF error)
    if (
      error.response?.status === 403 &&
      !originalRequest._csrfRetry &&
      isCsrfError(error)
    ) {
      originalRequest._csrfRetry = true;

      // Clear and refresh CSRF token
      clearCsrfToken();

      try {
        const newCsrfToken = await fetchCsrfToken();
        if (originalRequest.headers) {
          originalRequest.headers['x-csrf-token'] = newCsrfToken;
        }
        return api(originalRequest);
      } catch (csrfError) {
        console.error('CSRF token refresh failed:', csrfError);
        return Promise.reject(error);
      }
    }

    // #442: the server refused an open session because this parent has not agreed to the
    // school's T&Cs yet (403 CONSENT_REQUIRED). Tell AuthContext, which re-reads /auth/me so
    // ProtectedRoute swaps the app for the consent gate. Still reject: the caller's own error
    // handling runs as usual.
    if (error.response?.status === 403 && isConsentRequiredError(error)) {
      signalConsentRequired();
    }

    // If 401 and not already retrying, attempt token refresh
    // Skip for auth endpoints — their 401s are legitimate failures, not expired tokens
    //
    // #524: the refresh is SHARED (tokenRefresh.ts). Refresh tokens rotate, so when each 401 ran
    // its own refresh the loser was refused and cleared the winner's new tokens, logging the user
    // out with a valid session. And only a REFUSED refresh ends the session: one that got no
    // answer (offline, timeout, 5xx) leaves the tokens alone and just fails this request.
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/2fa/verify');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // This request was sent before a refresh finished: the stored token is already new.
      // Replay it once (the request interceptor puts the stored token on the replay); if that
      // newer token is refused too, the replay's 401 comes back here and refreshes.
      const storedAccess = localStorage.getItem('accessToken');
      if (!originalRequest._staleReplay && storedAccess && storedAccess !== bearerTokenOf(originalRequest)) {
        originalRequest._staleReplay = true;
        return api(originalRequest);
      }

      originalRequest._retry = true;
      try {
        await refreshAccessToken(`${API_URL}/auth/refresh`, storedAccess);
        return api(originalRequest);
      } catch (refreshError) {
        // The refresh got no answer: nothing proves the session is dead, so keep it.
        if (!isSessionEnded(refreshError)) {
          return Promise.reject(refreshError);
        }

        // Refresh refused — the stored session is dead, so clear it.
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // ⚠️ Do NOT bounce to login from a PUBLIC page.
        //
        // This redirect is a hard `window.location.href`, so it fires no matter which route
        // is mounted. A visitor holding a STALE token who opens a magic link was therefore
        // thrown to the login page before the page could render: `AuthProvider` fires
        // `/auth/me` on mount (AuthContext.tsx `initAuth`), the expired token 401s, the refresh
        // fails, and this ran — replacing a page that never needed auth at all. The token in
        // the URL *is* the credential.
        //
        // Clearing the tokens above is still correct and is what made it look like a one-off:
        // the failed attempt cleans up, so a second click works. It hits exactly the families
        // who have used the portal before, and reads to them as "the link is broken".
        //
        // On a public page we stop here and let the caller's own error handling run
        // (`initAuth`'s catch in AuthContext.tsx already degrades gracefully to logged-out).
        if (!isPublicPath(window.location.pathname)) {
          const schoolSlug = getCurrentSchoolSlug();
          const loginPath = schoolSlug ? `/${schoolSlug}/login` : '/login';
          window.location.href = loginPath;
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/** The access token a request was sent with (set by the request interceptor), or null. */
function bearerTokenOf(config: AxiosRequestConfig): string | null {
  // AxiosHeaders reads case-insensitively; a plain object (a caller's own headers) may not.
  const header =
    config.headers instanceof AxiosHeaders
      ? config.headers.get('Authorization')
      : (config.headers?.Authorization ?? config.headers?.authorization);
  return typeof header === 'string' ? header.replace(/^Bearer /, '') : null;
}

/** #442: a 403 from the server's consent gate (`details.code`, as `apiErrorCode` reads it). */
function isConsentRequiredError(error: AxiosError): boolean {
  const data = error.response?.data as { details?: { code?: unknown } } | undefined;
  return data?.details?.code === 'CONSENT_REQUIRED';
}

/**
 * Check if error is a CSRF-related error
 */
function isCsrfError(error: AxiosError): boolean {
  const data = error.response?.data as { message?: string } | undefined;
  const message = data?.message?.toLowerCase() || '';
  return message.includes('csrf');
}

export default api;

// ===========================================
// Public API Client (No Authentication)
// ===========================================

// Create separate axios instance for public endpoints (no auth token)
const publicApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor for CSRF only (no auth)
publicApi.interceptors.request.use(
  async (config) => {
    // Add CSRF token for state-changing methods
    if (config.method && CSRF_METHODS.includes(config.method.toUpperCase())) {
      try {
        const csrfPromise = getCsrfToken();
        const timeoutPromise = new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('CSRF token fetch timeout')), 5000)
        );
        const csrf = await Promise.race([csrfPromise, timeoutPromise]);
        config.headers['x-csrf-token'] = csrf;
      } catch (error) {
        console.warn('Could not get CSRF token for request:', error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for CSRF refresh only (no auth retry)
publicApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _csrfRetry?: boolean;
    };

    // Handle CSRF token mismatch (403 with CSRF error)
    if (
      error.response?.status === 403 &&
      !originalRequest._csrfRetry &&
      isCsrfError(error)
    ) {
      originalRequest._csrfRetry = true;
      clearCsrfToken();

      try {
        const newCsrfToken = await fetchCsrfToken();
        if (originalRequest.headers) {
          originalRequest.headers['x-csrf-token'] = newCsrfToken;
        }
        return publicApi(originalRequest);
      } catch (csrfError) {
        console.error('CSRF token refresh failed:', csrfError);
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Type-safe public API methods (no authentication)
export const publicClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    publicApi.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    publicApi.post<T>(url, data, config).then((res) => res.data),
};

// Type-safe API methods
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    api.delete<T>(url, config).then((res) => res.data),

  // Raw response (includes headers) - use for blob downloads
  getRaw: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get<T>(url, config),
};
